import React, { FC, useRef, useContext } from 'react';
import { getSearchFormClickTrackingLoc } from '../../../../../../modules/utils/searchFormHelper';
import { ShopPageDatesHeaderProps } from './shopPageDatesHeader.types';
import { StyledShopPageDatesHeaderDiv, StyledShopPageDateBoxDiv } from './shopPageDatesHeader.styles';
import { isCNLocale } from '@marriott/shared/mi-helper-utils';
import { IS_LOCALE_ZH_TW } from '../../../constants/ApplicationConstants';
import { formatDateStringBasedOnLocale } from '../../../../../../utils/src/helper';
import { useConditionalPageContextSERP } from '../../../../../../hooks';
import { ContentContext, ViewportContext } from '../../index.contexts';

interface ShopPageDateBoxProps {
  label: string;
  date: string;
  onClickHandler: () => void;
  focused: boolean;
  isADFPage: boolean;
  isMobileView: boolean;
  isCurrentLocaleCN: boolean;
  currentLocale: string;
  datesPlaceholderText: string;
}

const ShopPageDateBox: React.FC<ShopPageDateBoxProps> = ({
  label,
  date,
  onClickHandler,
  focused,
  isADFPage,
  isMobileView,
  isCurrentLocaleCN,
  currentLocale,
  datesPlaceholderText,
}) => {
  const mainBtn = useRef<HTMLButtonElement>(null);
  const dateLength = date?.length;
  const formattedDate = formatDateStringBasedOnLocale(date);

  return (
    <StyledShopPageDateBoxDiv
      data-component-name="a-shop-ShopPageDatesHeaderBox"
      data-testid="shop-ShopPageDatesHeaderBox"
      focused={focused}
      isCNLocale={isCurrentLocaleCN}
      currentLocale={currentLocale}
      dateLength={dateLength}
    >
      <button
        type="button"
        ref={mainBtn}
        className={`date-button  custom_click_track`}
        {...{ custom_click_track_value: `${getSearchFormClickTrackingLoc()}| Date Picker ${label} button |internal` }}
        aria-expanded={focused}
        onClick={onClickHandler}
      >
        <span
          className={`search__calendar-label calendar-text-ellipsis ${isADFPage ? '' : 'icon-nav---book'} t-label-xs`}
        >
          {label}
        </span>
        <span
          className={`${
            isMobileView
              ? 't-subtitle-inverse-l'
              : currentLocale === 'hi-IN'
              ? 't-subtitle-l'
              : dateLength >= 26
              ? 't-subtitle-s'
              : dateLength >= 16 && dateLength < 26
              ? 't-subtitle-m'
              : dateLength >= 12 && dateLength < 16
              ? 't-subtitle-l'
              : 't-subtitle-xl'
          } search-page-date-text ${!formattedDate ? 'gray-shade' : ''}`}
        >
          {formattedDate ? formattedDate : datesPlaceholderText}
        </span>
      </button>
    </StyledShopPageDateBoxDiv>
  );
};

const ShopPageDatesHeaderBox: FC<ShopPageDatesHeaderProps> = ({
  checkInDate,
  checkOutDate,
  onClickCheckIn,
  onClickCheckOut,
  focusedCheckIn = null,
  isADFPage = false,
}) => {
  const { checkInLabel, checkOutLabel, datesPlaceholderText } = useContext(ContentContext);
  const isMobileView = useContext(ViewportContext);

  const { currentLocale } = useConditionalPageContextSERP();
  const maxDateLength = checkInDate?.length > checkOutDate?.length ? checkInDate?.length : checkOutDate?.length;

  const isCurrentLocaleCN = isCNLocale(currentLocale) || currentLocale === IS_LOCALE_ZH_TW;

  return (
    <StyledShopPageDatesHeaderDiv isADFPage={isADFPage} dateLength={maxDateLength}>
      <ShopPageDateBox
        label={checkInLabel}
        date={checkInDate}
        onClickHandler={onClickCheckIn}
        focused={focusedCheckIn ?? false}
        isADFPage={isADFPage}
        isMobileView={isMobileView}
        isCurrentLocaleCN={isCurrentLocaleCN}
        currentLocale={currentLocale}
        datesPlaceholderText={datesPlaceholderText}
      />
      <ShopPageDateBox
        label={checkOutLabel}
        date={checkOutDate}
        onClickHandler={onClickCheckOut}
        focused={focusedCheckIn === null ? false : !focusedCheckIn}
        isADFPage={isADFPage}
        isMobileView={isMobileView}
        isCurrentLocaleCN={isCurrentLocaleCN}
        currentLocale={currentLocale}
        datesPlaceholderText={datesPlaceholderText}
      />
    </StyledShopPageDatesHeaderDiv>
  );
};

export default ShopPageDatesHeaderBox;
